import { ServiceException } from '@inter-ikea-kompis/exceptions';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';
import CacheStoreType from '../../enums/CacheStoreType.js';
/**
 * This class is used for storing and loading products from cache.
 */
export default class ProductCache {
    /**
     * Constructor.
     *
     * @param [locale] Locale.
     * @param [cache] Cache store.
     */
    constructor(locale, cache = null) {
        this.products = new Map();
        if (cache && !cache.getStore) {
            throw new ServiceException('Failed to instanciate facade. Expected second parameter to be of type CacheStore.');
        }
        this.locale = locale;
        this.cache = cache || new GlobalCacheStore();
    }
    /**
     * Returns multiple cached products.
     *
     * @param items Array of items.
     * @param fields Fields.
     * @returns Products and missing items.
     */
    getProducts(items, fields) {
        const searchableProductMap = this.getSearchableProductMap(fields);
        const products = [];
        const missing = [];
        for (const item of items) {
            const product = searchableProductMap[this.getProductCacheKey(item)];
            if (product) {
                products.push(product);
            }
            else {
                missing.push(item);
            }
        }
        return { products, missing };
    }
    /**
     * Adds multiple products.
     *
     * @param products Products.
     * @param fields Fields.
     */
    addProducts(products, fields) {
        for (const product of products) {
            this.addProduct(product, fields);
        }
    }
    /**
     * Removes products that has been added to this instance from cache.
     
     */
    removeProducts(products) {
        for (const product of products) {
            this.removeProduct(product);
        }
    }
    /**
     * Clears all products that has been added to this instance from cache.
     */
    clear() {
        for (const product of Array.from(this.products.values())) {
            this.removeProduct(product);
        }
    }
    /**
     * Returns map that can be used for when searching for a product item.
     *
     * @param fields Fields.
     * @returns Product items.
     */
    getSearchableProductMap(fields) {
        const store = this.cache.getStore(CacheStoreType.product);
        let merged = {};
        for (const key of Array.from(store.keys())) {
            if (key.startsWith('items-' + this.locale)) {
                const cachedFields = key.replace('items-' + this.locale + '-', '').split('_');
                let hasMatch = true;
                for (let i = 0, max = fields.length; i < max; i++) {
                    if (!cachedFields.includes(fields[i])) {
                        hasMatch = false;
                        break;
                    }
                }
                if (hasMatch) {
                    merged = Object.assign(merged, store.get(key));
                }
            }
        }
        return merged;
    }
    /**
     * Returns map that can be used for storing products.
     *
     * @param fields Fields.
     * @returns Product items.
     */
    getStorableProductMap(fields) {
        const store = this.cache.getStore(CacheStoreType.product);
        const cacheKey = this.getFieldsCacheKey(fields);
        const productMap = store.get(cacheKey);
        if (productMap) {
            return productMap;
        }
        const newProductMap = {};
        store.set(cacheKey, newProductMap);
        return newProductMap;
    }
    /**
     * Returns fields cache key.
     *
     * @param fields Fields.
     * @returns Cache key.
     */
    getFieldsCacheKey(fields) {
        return 'items-' + this.locale + '-' + fields.sort().join('_');
    }
    /**
     * Returns cache key for an item.
     *
     * @param item Product item.
     * @returns Cache key.
     */
    getProductCacheKey(item) {
        return item.type.toLowerCase() + item.id;
    }
    /**
     * Removes a product that has been added to this instance from cache.
     */
    removeProduct(product) {
        const store = this.cache.getStore(CacheStoreType.product);
        const [type, id] = product.itemId.split('-');
        const cacheKey = this.getProductCacheKey({ id, type });
        if (this.products.get(cacheKey)) {
            this.products.delete(cacheKey);
        }
        for (const items of Array.from(store.values())) {
            if (items[cacheKey]) {
                delete items[cacheKey];
            }
        }
    }
    /**
     * Adds a product to cache.
     *
     * @param product Product.
     * @param fields Fields.
     */
    addProduct(product, fields) {
        const productMap = this.getStorableProductMap(fields);
        const [type, id] = product.itemId.split('-');
        const cacheKey = this.getProductCacheKey({ id, type });
        this.products.set(cacheKey, product);
        productMap[cacheKey] = product;
    }
}
//# sourceMappingURL=ProductCache.js.map